import React, { useState, useRef, useEffect } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
  StyleProp,
  ViewStyle,
  Linking
} from 'react-native'
import { Buffer } from 'buffer'
import { WebView } from 'react-native-webview'
import { Ionicons } from '@expo/vector-icons'
import * as FileSystem from 'expo-file-system'
import * as Sharing from 'expo-sharing'
import { startActivityAsync } from 'expo-intent-launcher'
import Toast from 'react-native-toast-message'

interface PaystubPdfViewerProps {
  base64Data: string | null
  title?: string
  isLoading?: boolean
  onBack?: () => void
  onDownload?: () => void | Promise<void>
  style?: StyleProp<ViewStyle>
}

import { PDF_VIEWER_HTML } from './pdfViewerHtml'
import { colorPalette } from '../../static/styles/global'

export const PaystubPdfViewer: React.FC<PaystubPdfViewerProps> = ({
  base64Data,
  title = 'Paystub',
  isLoading = false,
  onBack,
  onDownload,
  style
}) => {
  const [downloading, setDownloading] = useState(false)
  const [webViewLoaded, setWebViewLoaded] = useState(false)
  const webViewRef = useRef<WebView>(null)

  const handleDownload = async () => {
    if (!base64Data) return

    try {
      setDownloading(true)
      if (onDownload) {
        await onDownload()
        return
      }

      if (Platform.OS === 'web') {
        const blob = new Blob([Buffer.from(base64Data, 'base64')], {
          type: 'application/pdf'
        })
        const url = URL.createObjectURL(blob)
        Linking.openURL(url)
      } else if (Platform.OS === 'ios') {
        let dataUri = `${FileSystem.documentDirectory}fms_downloaded_file.pdf`
        await FileSystem.writeAsStringAsync(dataUri, base64Data, {
          encoding: FileSystem.EncodingType.Base64
        })
        await Sharing.shareAsync(dataUri, {
          mimeType: 'application/pdf',
          dialogTitle: 'Share PDF'
        })
      } else if (Platform.OS === 'android') {
        let dataUri = `${FileSystem.documentDirectory}paystub_file.pdf`
        await FileSystem.writeAsStringAsync(dataUri, base64Data, {
          encoding: FileSystem.EncodingType.Base64
        })
        const cUri = await FileSystem.getContentUriAsync(dataUri)
        try {
          await startActivityAsync('android.intent.action.VIEW', {
            data: cUri,
            flags: 1,
            type: 'application/pdf'
          })
        } catch (intentErr) {
          await Sharing.shareAsync(dataUri, {
            mimeType: 'application/pdf',
            dialogTitle: 'Share PDF'
          })
        }
      }
    } catch (err: any) {
      console.error('Download error:', err)
      Toast.show({
        type: 'error',
        text1: 'Download Failed',
        text2: err?.message || 'Could not download paystub file'
      })
    } finally {
      setDownloading(false)
    }
  }

  // Inject Base64 paystub stream into Android local viewer once loaded
  useEffect(() => {
    if (Platform.OS === 'android' && webViewLoaded && base64Data) {
      const script = `
        if (typeof renderPdf === 'function') {
          renderPdf("${base64Data}");
        }
        true;
      `
      webViewRef.current?.injectJavaScript(script)
    }
  }, [webViewLoaded, base64Data])

  return (
    <View style={[styles.container, style]}>
      {/* Header Toolbar */}
      <View style={styles.toolbar}>
        {onBack ? (
          <TouchableOpacity onPress={onBack} style={styles.iconButton}>
            <Ionicons name="arrow-back" size={24} color={colorPalette.white} />
          </TouchableOpacity>
        ) : (
          <View style={{ width: 24 }} />
        )}

        <Text style={styles.title} numberOfLines={1}>
          {title}
        </Text>

        <TouchableOpacity
          onPress={handleDownload}
          style={styles.iconButton}
          disabled={downloading || !base64Data || isLoading}
        >
          {downloading ? (
            <ActivityIndicator size="small" color={colorPalette.white} />
          ) : (
            <Ionicons name="download-outline" size={24} color={colorPalette.white} />
          )}
        </TouchableOpacity>

      </View>

      {/* Embedded Document Viewport */}
      <View style={styles.viewerWrapper}>
        {isLoading || !base64Data ? (
          <View style={styles.centerBox}>
            <ActivityIndicator size="large" color="#0088CF" />
            <Text style={styles.loadingText}>Loading paystub...</Text>
          </View>
        ) : Platform.OS === 'ios' ? (
          // iOS: Native Apple WebKit Quartz PDF rendering 
          <WebView
            originWhitelist={['*']}
            source={{ uri: `data:application/pdf;base64,${base64Data}` }}
            style={styles.webview}
            scalesPageToFit={true}
            bounces={false}
          />
        ) : Platform.OS === 'web' ? (
          // Web: Native browser PDF viewer 
          <iframe
            src={`data:application/pdf;base64,${base64Data}#toolbar=0&navpanes=0&scrollbar=0`}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={title}
          />
        ) : (
          // Android: Offline bundled PDF.js viewer
          <WebView
            ref={webViewRef}
            originWhitelist={['*']}
            source={{
              html: PDF_VIEWER_HTML,
              baseUrl: 'https://localhost'
            }}
            style={styles.webview}
            javaScriptEnabled={true}
            domStorageEnabled={true}
            allowFileAccess={true}
            allowFileAccessFromFileURLs={true}
            allowUniversalAccessFromFileURLs={true}
            scalesPageToFit={true}
            onLoadEnd={() => {
              setWebViewLoaded(true)
              if (base64Data) {
                webViewRef.current?.injectJavaScript(`renderPdf("${base64Data}"); true;`)
              }
            }}
          />
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff'
  },
  toolbar: {
    height: 52,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    backgroundColor: colorPalette.primary,
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
    paddingTop: 10
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: colorPalette.white,
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 8
  },
  iconButton: {
    padding: 6,
  },
  viewerWrapper: {
    flex: 1,
    backgroundColor: '#525659'
  },
  webview: {
    flex: 1,
    backgroundColor: '#525659'
  },
  centerBox: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff'
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#6c757d'
  }
})

export default PaystubPdfViewer
